/**
 * PipelineFlow — hand-laid system diagram, replacement for PipelineDag.
 *
 * Why this exists: the Cytoscape-based PipelineDag was technically
 * correct but unreadable — 36 atomic nodes auto-laid in columns, no
 * narrative, no glanceability. This file ignores the per-node graph
 * and instead renders a small fixed set of GROUPED boxes that tell
 * the pipeline story left-to-right:
 *
 *   SOURCES → SCRAPE → PICK → SCORE+GRADE → SHIP
 *
 * Each grouped box aggregates the underlying nodes (its status pulls
 * the worst of the children; click opens a drawer listing them).
 *
 * Same data source (/api/pipeline-graph) — only the rendering layer
 * changes. Worker pipeline-graph endpoint can stay as-is.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { PipelineGraph, PipelineNode } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

interface ViewState {
  selectedGroup: GroupedBox | null;
}
const view: ViewState = { selectedGroup: null };

// ============================================================
// Grouping definition — collapses ~36 atomic nodes → ~12 grouped boxes
// ============================================================
type Phase = 0 | 1 | 2 | 3 | 4;

interface BoxSpec {
  id:        string;        // stable key
  phase:     Phase;
  title:     string;
  subtitle:  string;        // appears under title; short, declarative
  /** Predicate that decides whether a PipelineNode belongs in this box. */
  match:     (n: PipelineNode) => boolean;
  /** Show "(N)" badge with the underlying node count when > 1. */
  showCount: boolean;
}

const BOXES: readonly BoxSpec[] = [
  // -------- 0: SOURCES --------
  {
    id: "hr",
    phase: 0,
    title: "Hard Rock",
    subtitle: "Live odds + bet IDs",
    match: (n) => n.kind === "external",
    showCount: false,
  },
  // -------- 1: SCRAPE --------
  {
    id: "vps-scraper",
    phase: 1,
    title: "VPS Scraper",
    subtitle: "Pulls odds every 5 min",
    match: (n) => n.kind === "scraper",
    showCount: false,
  },
  {
    id: "odds-refresh",
    phase: 1,
    title: "Odds & Stats Refresh",
    subtitle: "Injuries, props, probables",
    match: (n) => n.kind === "agent" &&
                   (n.category === "data" || n.category === "probables"),
    showCount: true,
  },
  {
    id: "r2",
    phase: 1,
    title: "R2 Storage",
    subtitle: "Central data hub",
    match: (n) => n.kind === "store" && n.id === "r2-snapshots",
    showCount: false,
  },
  // -------- 2: PICK --------
  {
    id: "pickers",
    phase: 2,
    title: "AI Pickers",
    subtitle: "8 AM (WNBA 4 PM) · 6 sports",
    match: (n) => n.kind === "agent" && n.category === "picker",
    showCount: true,
  },
  // -------- 3: SCORE+GRADE --------
  {
    id: "scorer",
    phase: 3,
    title: "Parlay Scorer",
    subtitle: "AI confidence on each pick",
    match: (n) => n.id === "parlay-scorer-worker",
    showCount: false,
  },
  {
    id: "graders",
    phase: 3,
    title: "Graders",
    subtitle: "Settle bets post-game",
    match: (n) => n.kind === "agent" && n.category === "grader",
    showCount: true,
  },
  {
    id: "sync",
    phase: 3,
    title: "Dashboard Sync",
    subtitle: "Rebuilds dashboard.json",
    match: (n) => n.kind === "agent" && n.category === "sync",
    showCount: true,
  },
  // -------- 4: SHIP --------
  {
    id: "dash-json",
    phase: 4,
    title: "dashboard.json",
    subtitle: "Single source of truth",
    match: (n) => n.id === "dashboard-json",
    showCount: false,
  },
  {
    id: "user-app",
    phase: 4,
    title: "User App",
    subtitle: "thekitchen.cloud",
    match: (n) => n.id === "user-app",
    showCount: false,
  },
  {
    id: "console",
    phase: 4,
    title: "Operator Console",
    subtitle: "console.thekitchen.cloud",
    match: (n) =>
      n.id === "console-worker" ||
      n.id === "console-app" ||
      (n.kind === "agent" && n.category === "telemetry"),
    showCount: false,
  },
] as const;

const PHASE_META = [
  { label: "SOURCES",      caption: "Live odds + game feeds",         color: "#a1a1a6" },
  { label: "SCRAPE",       caption: "Pulled every 5 min",             color: "#5fa9ff" },
  { label: "PICK",         caption: "AI picks daily at 8 AM",         color: "#d39bff" },
  { label: "SCORE+GRADE",  caption: "Score AI, settle bets post-game", color: "#5fd47f" },
  { label: "SHIP",         caption: "To user dashboard",              color: "#a5a3f0" },
] as const;

// ============================================================
// Aggregation: PipelineGraph → GroupedBox[]
// ============================================================
interface GroupedBox extends BoxSpec {
  children:    PipelineNode[];
  status:      "ok" | "warn" | "error" | "idle";
  isFlowing:   boolean;     // any incoming or outgoing edge animating
  lastFire?:   string | null;
}

const STATUS_RANK: Record<string, number> = { ok: 0, idle: 1, warn: 2, error: 3 };

function buildGroups(g: PipelineGraph): GroupedBox[] {
  const flowingEndpoints = new Set<string>();
  for (const e of g.edges) {
    if (e.flowing) { flowingEndpoints.add(e.source); flowingEndpoints.add(e.target); }
  }
  return BOXES.map((b) => {
    const children = g.nodes.filter(b.match);
    let status: GroupedBox["status"] = "ok";
    let mostRecentFire: string | null = null;
    for (const c of children) {
      const s = (c.status ?? "ok") as GroupedBox["status"];
      if (STATUS_RANK[s]! > STATUS_RANK[status]!) status = s;
      if (c.last_fire && (!mostRecentFire || c.last_fire > mostRecentFire)) {
        mostRecentFire = c.last_fire;
      }
    }
    const isFlowing = children.some((c) => flowingEndpoints.has(c.id));
    return { ...b, children, status, isFlowing, lastFire: mostRecentFire };
  });
}

function flowingEdgeBetween(
  fromBox: GroupedBox,
  toBox: GroupedBox,
  edges: PipelineGraph["edges"],
): boolean {
  const fromIds = new Set(fromBox.children.map((c) => c.id));
  const toIds   = new Set(toBox.children.map((c) => c.id));
  return edges.some((e) => e.flowing && fromIds.has(e.source) && toIds.has(e.target));
}

// ============================================================
// Render
// ============================================================
export function renderPipelineFlow(onChange: () => void): TemplateResult {
  const e = store.get<PipelineGraph>("pipelineGraph");
  if (e.error) return errorView(e.error.message);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const groups = buildGroups(e.data);
  const flowingTotal = e.data.edges.filter((ed) => ed.flowing).length;
  const total = e.data.edges.length;

  // Build the inter-column "active flow" lines between consecutive phases.
  const columnFlows: boolean[] = [];
  for (let p = 0; p < 4; p++) {
    const from = groups.filter((g) => g.phase === p);
    const to   = groups.filter((g) => g.phase === p + 1);
    let any = false;
    outer: for (const f of from) for (const t of to) {
      if (flowingEdgeBetween(f, t, e.data!.edges)) { any = true; break outer; }
    }
    columnFlows.push(any);
  }

  return html`
    <div class="panel pipeline-flow-panel">
      <header class="panel__title pipeline-flow-panel__head">
        <span>Pipeline · ${groups.length} stations · ${flowingTotal}/${total} flows active</span>
        <span class="pipeline-flow-panel__head-actions">
          ${renderExplainButton({
            label: "Explain pipeline state",
            context: () => buildExplainContext(e.data!, groups),
          })}
        </span>
      </header>

      <div class="pipeline-flow">
        ${[0, 1, 2, 3, 4].map((phase, idx) => {
          const phaseMeta = PHASE_META[phase]!;
          const phaseGroups = groups.filter((g) => g.phase === phase);
          return html`
            <div class="pipeline-flow__col" style="--phase-color:${phaseMeta.color};">
              <div class="pipeline-flow__phase">
                <div class="pipeline-flow__phase-label">${phaseMeta.label}</div>
                <div class="pipeline-flow__phase-caption">${phaseMeta.caption}</div>
              </div>
              <div class="pipeline-flow__cards">
                ${phaseGroups.map((g) => groupedBoxView(g, onChange))}
              </div>
            </div>
            ${idx < 4
              ? html`<div class="pipeline-flow__arrow ${columnFlows[idx] ? "is-flowing" : ""}"
                          aria-hidden="true"></div>`
              : ""}
          `;
        })}
      </div>

      <footer class="pipeline-flow-legend">
        <span class="pipeline-flow-legend__item">
          <span class="status-dot is-ok"></span>healthy
        </span>
        <span class="pipeline-flow-legend__item">
          <span class="status-dot is-warn"></span>stale
        </span>
        <span class="pipeline-flow-legend__item">
          <span class="status-dot is-bad"></span>failed
        </span>
        <span class="pipeline-flow-legend__sep"></span>
        <span class="pipeline-flow-legend__hint">
          Click any station for the LaunchAgents and last-fire times that make it up.
        </span>
      </footer>

      ${view.selectedGroup
        ? groupDrawer(view.selectedGroup, () => { view.selectedGroup = null; onChange(); })
        : ""}
    </div>
  `;
}

function groupedBoxView(g: GroupedBox, onChange: () => void): TemplateResult {
  const statusDotCls =
    g.status === "ok"    ? "is-ok"   :
    g.status === "warn"  ? "is-warn" :
    g.status === "error" ? "is-bad"  : "is-idle";
  const ageMin = g.lastFire
    ? Math.max(0, Math.floor((Date.now() - Date.parse(g.lastFire)) / 60_000))
    : null;
  const ageLabel = ageMin == null ? "" :
    ageMin === 0 ? "fired now" :
    ageMin < 60  ? `fired ${ageMin}m ago` :
    ageMin < 60 * 24 ? `fired ${Math.floor(ageMin / 60)}h ago` :
                       `fired ${Math.floor(ageMin / (60 * 24))}d ago`;

  return html`
    <button class="pipeline-station ${g.isFlowing ? "is-flowing" : ""}"
            @click=${() => { view.selectedGroup = g; onChange(); }}>
      <div class="pipeline-station__head">
        <span class="status-dot ${statusDotCls} ${g.isFlowing ? "is-pulse" : ""}"></span>
        <span class="pipeline-station__title">${g.title}</span>
        ${g.showCount && g.children.length > 0
          ? html`<span class="pipeline-station__count">${g.children.length}</span>` : ""}
      </div>
      <div class="pipeline-station__subtitle">${g.subtitle}</div>
      ${ageLabel ? html`<div class="pipeline-station__age">${ageLabel}</div>` : ""}
    </button>
  `;
}

function groupDrawer(g: GroupedBox, onClose: () => void): TemplateResult {
  return html`
    <aside class="pipeline-flow-drawer glass">
      <header class="pipeline-flow-drawer__head">
        <h3>${g.title}</h3>
        <button class="pipeline-flow-drawer__close" @click=${onClose}>×</button>
      </header>
      <div class="pipeline-flow-drawer__body">
        <p class="pipeline-flow-drawer__subtitle">${g.subtitle}</p>
        ${g.children.length === 0
          ? html`<div class="pipeline-flow-drawer__empty">No underlying agents resolved yet.</div>`
          : html`
            <div class="pipeline-flow-drawer__children">
              ${g.children.map((c) => {
                const statusDotCls =
                  c.status === "ok"    ? "is-ok"   :
                  c.status === "warn"  ? "is-warn" :
                  c.status === "error" ? "is-bad"  : "is-idle";
                const cleanLabel = c.label
                  .replace(/^com\.yurysankov\./, "")
                  .replace(/\n/g, " · ");
                const ageMin = c.last_fire
                  ? Math.max(0, Math.floor((Date.now() - Date.parse(c.last_fire)) / 60_000))
                  : null;
                return html`
                  <div class="pipeline-flow-drawer__child">
                    <span class="status-dot ${statusDotCls}"></span>
                    <span class="pipeline-flow-drawer__child-label">${cleanLabel}</span>
                    <span class="pipeline-flow-drawer__child-age">
                      ${ageMin == null ? "—" :
                        ageMin === 0 ? "now" :
                        ageMin < 60  ? `${ageMin}m` :
                        ageMin < 60 * 24 ? `${Math.floor(ageMin / 60)}h` :
                                            `${Math.floor(ageMin / (60 * 24))}d`}
                    </span>
                  </div>
                `;
              })}
            </div>
          `}
      </div>
    </aside>
  `;
}

function buildExplainContext(g: PipelineGraph, groups: GroupedBox[]): string {
  const lines = [
    `Pipeline state snapshot (${g.generated_at}):`,
    `${groups.length} stations, ${g.nodes.length} underlying agents, ` +
      `${g.edges.filter((e) => e.flowing).length}/${g.edges.length} flows currently active.`,
    "",
    "Stations by health:",
    ...groups.map((b) =>
      `  [${b.status}] ${b.title} (${b.children.length} agent${b.children.length === 1 ? "" : "s"})` +
      (b.lastFire ? ` — last fire ${b.lastFire}` : ""),
    ),
  ];
  const stale = groups.filter((b) => b.status === "warn" || b.status === "error");
  if (stale.length === 0) lines.push("", "Everything healthy.");
  else lines.push("", `Concerning stations: ${stale.map((b) => b.title).join(", ")}`);
  return lines.join("\n");
}

function skeletonView(): TemplateResult {
  return html`
    <div class="panel" style="min-height:480px;display:flex;
                              align-items:center;justify-content:center;">
      <div class="skeleton" style="width:60%;height:14px;"></div>
    </div>
  `;
}
function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:480px;margin:0 auto;">
        No pipeline data yet. Console-reporter is loaded but hasn't
        produced its first snapshot — wait ~60s and refresh.
      </div>
    </div>
  `;
}
function errorView(msg: string): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title"><span>Pipeline</span></div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;margin-top:8px;">
        ${msg}
      </div>
    </div>
  `;
}
